import {
    Component,
    DestroyRef,
    inject,
    Input,
    OnChanges,
    signal,
    SimpleChanges,
    WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    ControlContainer,
    FormControl,
    ReactiveFormsModule,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CountryModel, TranslatedString } from 'lemunzo-api';
import { LocalizationService } from '../../services/localization.service';
import { FlagComponent } from '../flag/flag.component';
import { TranslationComponent } from '../translation/translation.component';

/**
 * Generic country select component for formControlName-based forms.
 * Uses the nearest ControlContainer and reacts to active language changes.
 */
@Component({
    selector: 'apcam-country-select',
    imports: [
        FlagComponent,
        MatSelectModule,
        ReactiveFormsModule,
        TranslatePipe,
        TranslationComponent,
    ],
    templateUrl: './country-select.component.html',
    styleUrl: './country-select.component.scss',
    viewProviders: [
        {
            provide: ControlContainer,
            useFactory: () => inject(ControlContainer, { skipSelf: true }),
        },
    ],
})
export class CountrySelectComponent implements OnChanges {
    @Input({ required: true })
    public formcontrolName!: string;

    @Input({ required: true })
    public label!: TranslatedString | string;

    @Input({ required: true })
    public countryList!: Map<string, CountryModel[]>;

    protected displayedCountryList: WritableSignal<CountryModel[]> = signal([]);
    protected selectedValue: string | null = null;

    protected get input(): FormControl | null {
        return (
            (this.controlContainer.control?.get([
                this.formcontrolName,
            ]) as FormControl | null) ?? null
        );
    }

    private get currentLanguage(): string {
        return this.localizationService.getCurrentLanguage();
    }

    private readonly destroyRef = inject(DestroyRef);
    private readonly controlContainer = inject(ControlContainer);
    private readonly localizationService = inject(LocalizationService);
    private readonly translateService = inject(TranslateService);

    ngOnChanges(_changes: SimpleChanges): void {
        if (_changes['countryList']) {
            this.updateDisplayedCountryList();
        }
    }

    constructor() {
        this.translateService.onLangChange
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.updateDisplayedCountryList());
    }

    protected getCountryName(code: string): string {
        if (!this.countryList) {
            return '';
        }

        return (
            this.countryList
                .get(this.currentLanguage)
                ?.find((x) => x.code === code)?.name ?? ''
        );
    }

    private updateDisplayedCountryList(): void {
        this.displayedCountryList.set(
            this.countryList?.get(this.currentLanguage) ?? []
        );
    }
}
